/**
 * Porta do Kontto Web completo, como uma tela de login: o Google é o botão principal (o navegador
 * vira mais um aparelho do sync), abrir um backup e ver o exemplo são caminhos secundários.
 *
 * "Entrar com o Google" é em dois passos, a conta ANTES da senha: popup do Google e conferência
 * de que há sincronização no Drive primeiro; a senha só é pedida quando vai servir pra abrir os
 * dados. O andamento aparece como passo a passo no próprio cartão, porque gerar a chave e montar
 * a base levam alguns segundos (às vezes um minuto na primeira vez).
 *
 * Conta sem sincronização mas com backup no Drive cai num cartão próprio (`drive-backup`): abre o
 * backup mais recente, avisando antes que o que for editado aqui não volta pro celular.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Wordmark } from './viewer/components/Wordmark';
import { NeedPassphraseError, backupWhen, isEncryptedBackup, type DriveBackupRef, type GoogleEntry, type JoinProgress, type JoinStep } from './session';

interface Props {
  /** passo 1: conta Google + Drive conferido; diz se há sincronização ou só o backup */
  onConnect: (onStep: (s: JoinStep) => void) => Promise<GoogleEntry>;
  /** passo 2: senha → chave → base reconstruída */
  onJoin: (passphrase: string, onStep: (s: JoinStep, progress?: JoinProgress) => void) => Promise<void>;
  /** passo 2 de quem só tem o backup no Drive: senha → backup aberto neste navegador */
  onOpenDriveBackup: (backup: DriveBackupRef, passphrase: string) => Promise<void>;
  /** desistiu entre os dois passos */
  onAbortJoin: () => Promise<void>;
  onOpenFile: (text: string, passphrase: string | null) => Promise<void>;
  onOpenDemo: () => Promise<void>;
}

type GoogleStage = JoinStep | 'passphrase';
type Flow =
  | { kind: 'idle' }
  | { kind: 'google'; stage: GoogleStage; email: string; progress?: JoinProgress }
  | { kind: 'drive-backup'; email: string; backup: DriveBackupRef; opening: boolean }
  | { kind: 'file'; name: string; text: string; opening: boolean }
  | { kind: 'demo' };

const GOOGLE_STEPS: Array<{ id: GoogleStage; label: string; busy?: string }> = [
  { id: 'google', label: 'Conta Google', busy: 'abrindo o Google…' },
  { id: 'checking', label: 'Sincronização no Drive', busy: 'procurando os seus dados…' },
  { id: 'passphrase', label: 'Senha da sincronização' },
  { id: 'key', label: 'Chave compartilhada', busy: 'gerando a chave, leva alguns segundos…' },
  { id: 'joining', label: 'Seus dados', busy: 'baixando e montando a sua base…' },
];
const ORDER: GoogleStage[] = GOOGLE_STEPS.map((s) => s.id);

/** O passo "Seus dados" fala do andamento sem jargão: nada de segmento, byte ou oplog. */
function joinText(p: JoinProgress | undefined): string {
  if (!p || p.total === 0) return 'procurando o seu histórico…';
  if (p.phase === 'download') return `trazendo o seu histórico… ${Math.round((p.done / p.total) * 100)}%`;
  if (p.phase === 'apply') return 'organizando as suas contas e lançamentos…';
  return 'quase lá…';
}
/** Fração da barra: baixar é a maior parte; organizar e abrir completam. */
function joinFraction(p: JoinProgress | undefined): number | null {
  if (!p || p.total === 0) return null;
  if (p.phase === 'download') return (p.done / p.total) * 0.8;
  if (p.phase === 'apply') return 0.9;
  return 1;
}
const BUSY_STAGES = new Set<GoogleStage>(['google', 'checking', 'key', 'joining']);

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function Spinner() {
  return <span className="gate-spinner" aria-hidden="true" />;
}

/** Segundos desde que o passo atual começou; só aparece depois de 3 s. */
function useElapsed(active: boolean): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) { setN(0); return; }
    const t0 = Date.now();
    const id = window.setInterval(() => setN(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return n;
}

export function Gate({ onConnect, onJoin, onOpenDriveBackup, onAbortJoin, onOpenFile, onOpenDemo }: Props) {
  const [flow, setFlow] = useState<Flow>({ kind: 'idle' });
  const [pass, setPass] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const busy = (flow.kind === 'google' && BUSY_STAGES.has(flow.stage)) || ((flow.kind === 'file' || flow.kind === 'drive-backup') && flow.opening) || flow.kind === 'demo';
  const elapsed = useElapsed(busy);

  async function connect() {
    setError(null);
    setFlow({ kind: 'google', stage: 'google', email: '' });
    try {
      const entry = await onConnect((s) => setFlow((f) => (f.kind === 'google' ? { ...f, stage: s } : f)));
      setPass('');
      setFlow(entry.kind === 'sync'
        ? { kind: 'google', stage: 'passphrase', email: entry.email }
        : { kind: 'drive-backup', email: entry.email, backup: entry.backup, opening: false });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setFlow({ kind: 'idle' });
    }
  }

  async function openDriveBackup(e: FormEvent) {
    e.preventDefault();
    if (flow.kind !== 'drive-backup') return;
    setError(null);
    setFlow({ ...flow, opening: true });
    try {
      await onOpenDriveBackup(flow.backup, pass);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setFlow({ ...flow, opening: false });
    }
  }

  async function join(e: FormEvent) {
    e.preventDefault();
    if (flow.kind !== 'google') return;
    const p = pass;
    setError(null);
    setFlow({ ...flow, stage: 'key' });
    try {
      await onJoin(p, (s, progress) => setFlow((f) => (f.kind === 'google' ? { ...f, stage: s, progress: progress ?? (s === f.stage ? f.progress : undefined) } : f)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setFlow({ ...flow, stage: 'passphrase' });
    }
  }

  async function pickFile(file: File) {
    const text = await file.text();
    setError(null);
    if (isEncryptedBackup(text)) { setFlow({ kind: 'file', name: file.name, text, opening: false }); setPass(''); return; }
    setFlow({ kind: 'file', name: file.name, text, opening: true });
    try { await onOpenFile(text, null); } catch (e) { setError(e instanceof Error ? e.message : String(e)); setFlow({ kind: 'idle' }); }
  }

  async function openFile(e: FormEvent) {
    e.preventDefault();
    if (flow.kind !== 'file') return;
    setError(null);
    setFlow({ ...flow, opening: true });
    try {
      await onOpenFile(flow.text, pass);
    } catch (err) {
      setError(err instanceof NeedPassphraseError ? 'Esse arquivo tem senha.' : err instanceof Error ? err.message : String(err));
      setFlow({ ...flow, opening: false });
    }
  }

  async function demo() {
    setError(null);
    setFlow({ kind: 'demo' });
    try { await onOpenDemo(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); setFlow({ kind: 'idle' }); }
  }

  function cancel() {
    const wasGoogle = flow.kind === 'google' || flow.kind === 'drive-backup';
    setFlow({ kind: 'idle' }); setError(null); setPass('');
    if (wasGoogle) void onAbortJoin();
  }

  const passField = (label: string, submitLabel: string) => (
    <>
      <label className="block">
        <span className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-mute">{label}</span>
        <input type="password" autoFocus value={pass} onChange={(e) => setPass(e.target.value)} disabled={busy} placeholder="Senha" data-testid="passphrase"
          className="mt-1.5 w-full rounded-xl border border-rule bg-paper px-4 py-3 text-[15px] text-ink outline-none focus:border-teal disabled:opacity-60" />
      </label>
      {error && <p className="mt-3 rounded-xl bg-neg-pale px-3 py-2 text-[13px] text-neg" data-testid="gate-error">{error}</p>}
      <div className="mt-4 flex items-center justify-end gap-2">
        <button type="button" onClick={cancel} disabled={busy} className="rounded-xl px-4 py-2.5 text-[14px] text-mute hover:text-ink disabled:opacity-60">Cancelar</button>
        <button type="submit" disabled={busy || pass.length === 0} data-testid="passphrase-submit" className="rounded-xl bg-teal px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-teal-dark disabled:opacity-60">{submitLabel}</button>
      </div>
    </>
  );

  return (
    <main className="flex min-h-screen flex-col bg-paper px-6 py-8">
      <header className="app-gate mx-auto flex w-full max-w-5xl items-center justify-between">
        <Wordmark size={28} />
        <span className="text-[11px] font-medium tracking-[0.15em] text-mute">WEB · BETA</span>
      </header>

      <section className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10">
        <div className="rounded-3xl border border-divider bg-surface p-7 shadow-[0_8px_30px_rgb(20_33_38_/_.06)] sm:p-8">

          {flow.kind === 'idle' && (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal">Kontto no computador</p>
              <h1 className="mt-2 text-[26px] font-semibold leading-tight tracking-[-0.02em] text-ink">Entrar no Kontto</h1>
              <p className="mt-2 text-[14px] leading-relaxed text-mute">Os mesmos dados do celular, agora também pra lançar e editar.</p>

              <button type="button" onClick={() => void connect()} data-testid="google-connect"
                className="mt-7 flex h-12 w-full items-center justify-center gap-3 rounded-xl bg-teal text-[15px] font-semibold text-white transition hover:bg-teal-dark">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white"><GoogleMark /></span>
                Entrar com o Google
              </button>
              <p className="mt-2.5 text-center text-[12px] text-mute">Usa a conta Google conectada no celular (Configurações › Backup, Pro).</p>

              <div className="my-6 flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.12em] text-mute"><span className="h-px flex-1 bg-divider" />ou<span className="h-px flex-1 bg-divider" /></div>

              <button type="button" onClick={() => fileInput.current?.click()} data-testid="open-file"
                className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-rule bg-surface text-[14px] font-semibold text-ink transition hover:border-teal">
                Abrir um arquivo de backup
              </button>
              <p className="mt-2 text-center text-[12px] text-mute">.kontto ou .json exportado pelo app. A edição fica só neste navegador.</p>

              <p className="mt-6 text-center text-[13px] text-mute">
                Só quer conhecer as telas?{' '}
                <button type="button" onClick={() => void demo()} className="font-semibold text-teal hover:underline">Ver com dados de exemplo</button>
              </p>
              {error && <p className="mt-5 rounded-xl bg-neg-pale px-4 py-3 text-[13px] text-neg" data-testid="gate-error">{error}</p>}
            </>
          )}

          {flow.kind === 'google' && (
            <form onSubmit={join}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal">Entrando com o Google</p>
              <h1 className="mt-2 text-[22px] font-semibold leading-tight tracking-[-0.02em] text-ink">{flow.email ? <>Olá, <span className="break-all">{flow.email}</span></> : 'Conectando…'}</h1>
              <ol className="gate-steps mt-6" aria-label="Andamento">
                {GOOGLE_STEPS.map((s, i) => {
                  const cur = ORDER.indexOf(flow.stage);
                  const state = i < cur ? 'done' : i === cur ? 'current' : 'todo';
                  const isBusy = state === 'current' && BUSY_STAGES.has(s.id);
                  return (
                    <li key={s.id} className={`is-${state}`}>
                      <span className="gate-step-mark" aria-hidden="true">{state === 'done' ? '✓' : isBusy ? <Spinner /> : i + 1}</span>
                      <span className="gate-step-body">
                        <span className="gate-step-label">{s.label}</span>
                        {isBusy && <span className="gate-step-note" data-testid="busy">{s.id === 'joining' ? joinText(flow.progress) : s.busy}{elapsed >= 3 ? ` ${elapsed} s` : ''}</span>}
                        {s.id === 'passphrase' && state === 'current' && (
                          <span className="gate-step-form">
                            <span className="block text-[13px] leading-relaxed text-mute">A senha de backup do celular é a chave que abre os seus dados. Fica só neste navegador, e você digita uma vez.</span>
                            <span className="mt-3 block">{passField('Senha', 'Entrar')}</span>
                          </span>
                        )}
                        {s.id === 'joining' && state === 'current' && <span className="gate-step-note is-hint">Na primeira vez pode levar perto de um minuto, conforme o tamanho do seu histórico. Pode deixar a aba aberta.</span>}
                      </span>
                    </li>
                  );
                })}
              </ol>
              {busy && (() => {
                const frac = flow.kind === 'google' && flow.stage === 'joining' ? joinFraction(flow.progress) : null;
                return frac === null
                  ? <div className="gate-progress mt-6" role="progressbar" aria-label="Carregando" />
                  : <div className="gate-progress is-known mt-6" role="progressbar" aria-label="Carregando" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(frac * 100)}><i style={{ width: `${Math.round(frac * 100)}%` }} /></div>;
              })()}
              {busy && <div className="mt-4 text-right"><button type="button" onClick={cancel} className="text-[13px] text-mute hover:text-ink">Cancelar</button></div>}
            </form>
          )}

          {flow.kind === 'drive-backup' && (() => {
            const when = backupWhen(flow.backup.stamp);
            return (
              <form onSubmit={openDriveBackup} data-testid="drive-backup">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal">Entrando com o Google</p>
                <h1 className="mt-2 text-[22px] font-semibold leading-tight tracking-[-0.02em] text-ink">Olá, <span className="break-all">{flow.email}</span></h1>
                {flow.opening ? (
                  <>
                    <p className="mt-4 flex items-center gap-2 text-[14px] text-ink-soft" data-testid="busy"><Spinner /> Baixando e abrindo o seu backup…{elapsed >= 3 ? ` ${elapsed} s` : ''}</p>
                    <div className="gate-progress mt-5" role="progressbar" aria-label="Carregando" />
                  </>
                ) : (
                  <>
                    <p className="mt-4 text-[14px] leading-relaxed text-ink-soft">Esta conta não tem a sincronização ligada, mas achei o seu backup{when ? <> de <strong className="font-semibold text-ink">{when}</strong></> : null}. Dá pra abrir ele aqui.</p>
                    <p className="mt-3 rounded-xl bg-teal-pale px-3 py-2.5 text-[13px] leading-relaxed text-ink-soft" data-testid="drive-backup-note">O que você lançar ou editar aqui fica só neste navegador e não volta pro celular. Pra usar os dois juntos, ligue “Sincronizar aparelhos” no celular (Configurações › Backup) e entre de novo.</p>
                    <div className="mt-5">{passField('Senha de proteção', 'Abrir o backup')}</div>
                  </>
                )}
              </form>
            );
          })()}

          {flow.kind === 'file' && (
            <form onSubmit={openFile}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal">Abrir um arquivo</p>
              <h1 className="mt-2 break-all text-[20px] font-semibold leading-tight tracking-[-0.02em] text-ink">{flow.name}</h1>
              {flow.opening ? (
                <>
                  <p className="mt-4 flex items-center gap-2 text-[14px] text-ink-soft" data-testid="busy"><Spinner /> Abrindo e montando os seus dados…{elapsed >= 3 ? ` ${elapsed} s` : ''}</p>
                  <div className="gate-progress mt-5" role="progressbar" aria-label="Carregando" />
                </>
              ) : (
                <>
                  <p className="mt-2 text-[13px] leading-relaxed text-mute">Esse arquivo tem senha: a que você definiu no app ao criar o backup.</p>
                  <div className="mt-5">{passField('Senha do arquivo', 'Abrir')}</div>
                </>
              )}
            </form>
          )}

          {flow.kind === 'demo' && (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal">Dados de exemplo</p>
              <p className="mt-4 flex items-center gap-2 text-[14px] text-ink-soft" data-testid="busy"><Spinner /> Montando o exemplo…</p>
              <div className="gate-progress mt-5" role="progressbar" aria-label="Carregando" />
            </>
          )}
        </div>

        <p className="mt-5 text-center text-[12px] leading-relaxed text-mute">Cifrado de ponta a ponta: os dados só abrem com a sua senha, e ela nunca sai do seu aparelho. O Kontto não lê nada.</p>
        <input ref={fileInput} type="file" accept=".kontto,.json,application/json" className="hidden" data-testid="file-input"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void pickFile(f); e.target.value = ''; }} />
      </section>
    </main>
  );
}
